import { useEffect, useRef } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { PlanningEntityIdentifier, PlanningEntityType } from 'generated';

import { Box } from '@mui/material';

import DropIndicator from '../../components/DropIndicator/DropIndicator';

interface DropZoneV2Props {
  id: string;
  scheduleStart: string;
  start: string;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
}

function DropzonePragmatic({
  scheduleStart,
  start,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier
}: DropZoneV2Props) {
  'use memo';
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        scheduleStart,
        start,
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier
      }),
      onDragEnter: () => el.setAttribute('data-drag-over', ''),
      onDragLeave: () => el.removeAttribute('data-drag-over'),
      onDrop: () => el.removeAttribute('data-drag-over')
    });
    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    scheduleStart,
    start,
    planningEntityId,
    planningEntityType,
    planningEntityIdentifier
  ]);

  return (
    <Box
      component="li"
      ref={ref}
      display="flex"
      flexShrink={0}
      className="dropzone"
      justifyContent="center"
      alignItems="center"
      sx={{
        boxSizing: 'border-box',
        minHeight: '8px',
        width: '100%',
        color: '#6b6b6b',
        fontSize: '10px',
        height: '30px',
        margin: '-10px 0',
        position: 'relative',
        zIndex: 1,
        '&:first-of-type': {
          marginTop: '-20px'
        },
        '& .dropzone__indicator': { display: 'none' },
        '&[data-drag-over] .dropzone__indicator': { display: 'block' }
      }}
    >
      <DropIndicator className="dropzone__indicator" />
    </Box>
  );
}

export default DropzonePragmatic;
